import { useEffect, useMemo, useRef, useState } from "react";
import { Bell, CheckCheck, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/DropdownMenu";
import { NotificationItem } from "@/components/NotificationItem";
import {
  useInfiniteNotifications,
  useMarkAllRead,
  useMarkOneRead,
  useUnreadNotificationCount,
} from "@/hooks/useNotifications";
import { api, Notification, programsApi } from "@/services/api";
import { useDrawer } from "@/context/DrawerContext";
import { useAuth } from "@/hooks/useAuth";
import { useActiveWorkspace } from "@/hooks/useActiveWorkspace";
import { hasRole } from "@/utils/roleUtils";
import { MembershipRole } from "@/constants/roles";
import {
  MESSAGE_NOTIFICATION_TYPES,
  extractMeetingId,
  extractProgramId,
  resolveAchieverNotificationTarget,
} from "@/utils/notificationTargets";

// Advisor-facing "review the achiever's session prep" notifications — open the
// read-only SessionPrepDrawer directly instead of navigating to /sessions.
const SESSION_PREP_NOTIFICATION_TYPES = new Set(["session_prep_ready", "prep_notes_submitted"]);

/**
 * The Expert Program this notification belongs to, or null for a PAB one.
 *
 * Expert-program notifications carry `program_external_id` in their metadata;
 * PAB ones never do. That is a more reliable signal than looking the program up
 * in a cache — the achiever's programs list is only fetched for achievers, so
 * an expert has nothing to look up.
 */
const expertProgramExternalId = (notification: Notification): string | null => {
  try {
    const meta = JSON.parse(notification.metadataJson ?? "{}");
    const value = meta?.program_external_id;
    return typeof value === "string" && value.length > 0 ? value : null;
  } catch {
    return null;
  }
};
// Backend actionUrl points these at /pab or /pab/{session_id}, but product
// wants them landing somewhere more useful — override the destination here
// rather than relying on the backend-supplied actionUrl for these types.
// Achiever clicks never reach this map: they are resolved into a journey tab
// (or a drawer) by resolveAchieverNotificationTarget instead.
const ROUTE_OVERRIDES: Record<string, string> = {
  new_matching_round_available: "/home",
  multiple_pending_requests: "/home",
  task_overdue: "/goals",
};

export const NotificationPanel = () => {
  const navigate = useNavigate();
  const {
    openMessagesListDrawer,
    openSessionPrepDrawer,
    openAchieverSessionPrepDrawer,
    openAchieverSessionSummaryDrawer,
  } = useDrawer();
  const { user } = useAuth();
  // Journey Portfolio routing is an achiever-workspace concept — the advisor
  // side has no journeys and keeps following the backend actionUrl (/sessions,
  // /pab/{id}, …). A dual-role user must therefore be branched on the ACTIVE
  // workspace, not hasRole(): both roles are true at once for them, so keying
  // off the role alone sends an advisor to the achiever journey pages.
  const activeWorkspace = useActiveWorkspace();
  const isAdvisorWorkspace = activeWorkspace === "advisor";
  const isAdvisor = hasRole(user, "advisor") && isAdvisorWorkspace;
  const isAchiever = hasRole(user, "achiever") && !isAdvisorWorkspace;
  const [isOpen, setIsOpen] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const queryClient = useQueryClient();

  const { data: unreadData } = useUnreadNotificationCount();
  const unreadCount = unreadData?.unreadCount ?? 0;

  const { data, hasNextPage, fetchNextPage, isFetchingNextPage, isLoading } =
    useInfiniteNotifications(isOpen);
  const markAllRead = useMarkAllRead();
  const markOneRead = useMarkOneRead();

  const pages = data?.pages ?? [];
  const notifications = useMemo(() => pages.flatMap((p) => p.items), [pages]);
  const panelUnreadCount = pages[0]?.unreadCount ?? unreadCount;

  // PAB-vs-Expert per program id, so an achiever's notification can be routed
  // to the right journey detail page. Same query key useExpertPrograms uses, so
  // this shares that cache rather than firing its own request.
  const { data: programsData } = useQuery({
    queryKey: ["programs"],
    queryFn: programsApi.list,
    enabled: !!user && isAchiever,
    staleTime: 5 * 60 * 1000,
  });
  // Only the journeys this user is the ACHIEVER of -- a dual-role user's
  // advisor programs are not journeys they own, and including them would break
  // the resolver's "exactly one journey" fallback for older notifications.
  const programsById = useMemo(
    () =>
      new Map(
        (programsData?.programs ?? [])
          .filter((p) => p.role === MembershipRole.ACHIEVER)
          .map((p) => [p.id, p]),
      ),
    [programsData],
  );

  // Infinite scroll via IntersectionObserver on bottom sentinel
  useEffect(() => {
    if (!isOpen || !sentinelRef.current) return;
    const el = sentinelRef.current;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage();
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [isOpen, hasNextPage, isFetchingNextPage, fetchNextPage]);

  /**
   * Which of the achiever's journeys owns this meeting. Needed for
   * notifications queued before `program_id` was added to their metadata:
   * they carry only `meeting_id`, and meetings are only addressable per
   * program (`GET /pab-sessions/{programId}/meetings/{meetingId}`), so the
   * candidates are tried in turn -- a wrong guess 403/404s harmlessly.
   * Typically one or two programs, and each hit is cached by react-query.
   */
  const findProgramForMeeting = async (meetingId: number): Promise<number | null> => {
    for (const program of programsById.values()) {
      try {
        await queryClient.fetchQuery({
          queryKey: ["meeting", meetingId, program.id],
          queryFn: () => api.pabSessions.getMeeting(program.id, meetingId),
          staleTime: 2 * 60 * 1000,
        });
        return program.id;
      } catch {
        // Not this program's meeting — try the next.
      }
    }
    return null;
  };

  const onNotificationClick = async (notification: Notification) => {
    // Message-type notifications open the MessagesListDrawer instead of navigating
    if (MESSAGE_NOTIFICATION_TYPES.has(notification.notificationType)) {
      setIsOpen(false);
      openMessagesListDrawer();
      if (!notification.isRead) markOneRead.mutate(notification.id);
      return;
    }
    // Expert Program session prep, which inverts PAB's roles: the experts
    // author the briefing and the cohort reads it. Both land on the same
    // artifact via ?prep=<sessionId>; ExpertSessionPrep decides editable vs
    // read-only from the server's `can_edit`, so this only has to pick the
    // right surface for the workspace the user is in.
    if (SESSION_PREP_NOTIFICATION_TYPES.has(notification.notificationType)) {
      const ext = expertProgramExternalId(notification);
      const meetingId = extractMeetingId(notification.metadataJson);
      if (ext && meetingId !== null) {
        setIsOpen(false);
        navigate(
          isAdvisor
            ? `/programs/${ext}/details?prep=${meetingId}`
            : `/programs/${ext}/journey?prep=${meetingId}`,
        );
        if (!notification.isRead) markOneRead.mutate(notification.id);
        return;
      }
    }
    // Advisor session-prep notifications open the read-only drawer in place,
    // instead of navigating to the Sessions page — same pattern as the
    // message-type case above: always open, best-effort populate content.
    if (isAdvisor && SESSION_PREP_NOTIFICATION_TYPES.has(notification.notificationType)) {
      const meetingId = extractMeetingId(notification.metadataJson);
      setIsOpen(false);
      openSessionPrepDrawer(meetingId);
      if (!notification.isRead) markOneRead.mutate(notification.id);
      return;
    }
    // Achievers never follow the raw actionUrl ("/pab/{id}", "/sessions",
    // "/goals"): every destination is a tab of the journey this notification
    // belongs to, and session-specific ones open a drawer in place.
    if (isAchiever) {
      if (!notification.isActionable) return;
      setIsOpen(false);

      // Older notifications name a meeting but not its program — ask the API
      // which journey the meeting belongs to before falling back to a guess.
      const meetingId = extractMeetingId(notification.metadataJson);
      const programIdFallback =
        extractProgramId(notification) === null && meetingId !== null
          ? await findProgramForMeeting(meetingId)
          : null;

      const target = resolveAchieverNotificationTarget(notification, programsById, {
        programIdFallback,
      });
      if (target.kind === "none") return;
      switch (target.kind) {
        case "messages":
          openMessagesListDrawer();
          break;
        case "session-prep":
          openAchieverSessionPrepDrawer(target.meetingId, target.programId);
          break;
        case "session-summary":
          openAchieverSessionSummaryDrawer(target.meetingId, target.programId);
          break;
        case "route":
          navigate(target.path);
          break;
      }
      if (!notification.isRead) markOneRead.mutate(notification.id);
      return;
    }
    const routeOverride = ROUTE_OVERRIDES[notification.notificationType];
    if (notification.isActionable && routeOverride) {
      navigate(routeOverride);
      setIsOpen(false);
      if (!notification.isRead) markOneRead.mutate(notification.id);
      return;
    }
    if (notification.isActionable && notification.actionUrl) {
      navigate(notification.actionUrl);
      setIsOpen(false);
      if (!notification.isRead) markOneRead.mutate(notification.id);
    }
    // Non-actionable notifications are never marked read on click —
    // they can only be cleared via "Mark all as read".
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <button
          data-tour-id="nav-notifications"
          className="relative flex h-8 w-8 items-center justify-center rounded-full border border-theme-border bg-theme-bg-surface-raised hover:bg-theme-bg-hover transition-colors"
          title="Notifications"
        >
          <Bell className="h-4 w-4 text-theme-text-secondary" />
          {unreadCount > 0 && (
            <span className="absolute -right-1 -top-1 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-[var(--color-primary-750)] px-1 text-[10px] font-bold text-white leading-none">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="w-[420px] max-h-[580px] bg-theme-bg-surface border border-theme-border shadow-2xl rounded-2xl p-0 flex flex-col overflow-hidden"
        sideOffset={10}
      >
        {/* Panel header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-theme-border shrink-0 bg-theme-bg-surface">
          <div className="flex items-center gap-2.5">
            <span className="font-inter font-bold text-[15px] text-theme-text-heading tracking-tight">
              Notifications
            </span>
            {panelUnreadCount > 0 && (
              <span className="rounded-full bg-[var(--color-primary-750)] px-2 py-0.5 text-[10px] font-bold text-white leading-4">
                {panelUnreadCount > 99 ? "99+" : panelUnreadCount}
              </span>
            )}
          </div>
          {panelUnreadCount > 0 && (
            <button
              type="button"
              onClick={() => markAllRead.mutate()}
              disabled={markAllRead.isPending}
              className="flex items-center gap-1.5 font-inter text-[12px] font-semibold text-theme-text-secondary hover:text-theme-text transition-colors disabled:opacity-50"
            >
              <CheckCheck className="h-3.5 w-3.5" />
              Mark all read
            </button>
          )}
        </div>

        {/* Scrollable notification list */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-5 w-5 animate-spin text-theme-text-muted" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-3 py-16 text-theme-text-muted">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-theme-bg-surface-raised">
                <Bell className="h-6 w-6 opacity-30" />
              </div>
              <div className="text-center">
                <p className="font-inter text-sm font-semibold text-theme-text-secondary">
                  No notifications yet
                </p>
                <p className="font-inter text-xs text-theme-text-muted mt-1">
                  We&apos;ll notify you when something happens
                </p>
              </div>
            </div>
          ) : (
            <div>
              {notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onClick={onNotificationClick}
                  /* An expert is being asked to WRITE the briefing; everyone
                     else is being pointed at one to read. PAB notifications
                     keep the default "View Session Prep" either way, since
                     there the advisor is the reader. */
                  sessionPrepCta={
                    isAdvisor && expertProgramExternalId(notification)
                      ? "Prepare Session Prep"
                      : "View Session Prep"
                  }
                />
              ))}
              <div ref={sentinelRef} className="h-1 w-full" />
              {isFetchingNextPage && (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-4 w-4 animate-spin text-theme-text-muted" />
                </div>
              )}
            </div>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
